<template lang="pug">
    .card.right-side-card.booking-card
        h5.card-header Game Master Booking
        .card-body
            re-select(label="Select Game" v-model.trim="$v.fields.game.$model" :validations="validations.game" :v="$v.fields.game" @input="$v.fields.game.$reset")
                option(value="" disabled hidden) Select game...
                option(v-for="game in games" :value="game.name") {{ game.title }}
            re-input(label="Number of Players" type="number" :min="minParticipants" step="1" v-model.trim="$v.fields.players.$model" :validations="validations.players" :v="$v.fields.players" @input="$v.fields.players.$reset")
            re-input(label="Number of Hours" type="number" min="2" :max="maxHours" step="1" v-model.trim="$v.fields.hours.$model" :validations="validations.hours" :v="$v.fields.hours" @input="$v.fields.hours.$reset")
            .form-group
                label Date
                b-form-datepicker(v-model="$v.fields.date.$model" :state="invalidDate ? false : null" menu-class="w-100" calendar-width="100%" :min="minDate" :max="maxDate" :disabled="$v.fields.hours.$invalid" :date-disabled-fn="dateDisabled")
                .error.invalid-feedback(v-if="invalidDate") Date is required
            re-select(label="Time" v-model.trim="$v.fields.timeOfDay.$model" :validations="validations.timeOfDay" :v="$v.fields.timeOfDay" @input="$v.fields.timeOfDay.$reset" :disabled="$v.fields.hours.$invalid || $v.fields.date.$invalid")
                option(value="" disabled hidden) Select time...
                option(v-for="timeOfDay in availableTimesOfDay" :value="timeOfDay.value") {{ timeOfDay.text }}
            gmap-autocomplete.form-group(@place_changed="addressChanged" slot-ref-name="addressInput" :select-first-on-enter="true" :class="{ 'is-invalid form-group-error': invalidAddress }")
                template(v-slot="slotProps")
                    label Address for Game
                    input.form-control(ref="addressInput" :class="{ 'is-invalid': invalidAddress }" autocomplete="off" v-bind="slotProps.attrs" v-on="slotProps.listeners" @input="$v.fields.address.$model = ''")
                    .error.invalid-feedback(v-if="invalidAddress") Address is required
            template(v-if="!fullBooking")
                p Click on Check Availability button to see available time slots.
                re-toast-alert.mb-4(v-if="unavailable" type="danger") Booking is not available for the selected Date and Time
                re-button.btn-outline-primary.btn-lg(@click="checkAvailability" :loading="checkingAvailability") Check Availability
            template(v-else)
                label Available Time Slots
                .row.booking-time-slots(:class="{ 'is-invalid': $v.fields.slot.$dirty && $v.fields.slot.$invalid }")
                    .col-6(v-for="(slot, value) in availableSlots")
                        .btn.btn-primary.btn-sm(@click="fields.slot = value" :class="{ selected: fields.slot == value }") {{ slot }}
                p(v-if="timezone") Time slots are displayed in {{ timezone.name }} time zone.
                .invalid-feedback.mb-4(v-if="$v.fields.slot.$dirty && $v.fields.slot.$invalid")
                    div Time slot is required
                label Provide Add-Ons
                template(v-for="upgrade in upgrades")
                    .two-cols.booking-addons(v-if="upgrade.game == fields.game")
                        re-checkbox(v-model="fields.upgrades[upgrade.name]") {{ upgrade.title }}
                        .booking-addons__price {{ upgrade.price | currency }}
                re-textarea(label="Additional Comments" v-model.trim="fields.notes")
                hr
                .two-cols.booking-subtotal
                    span Subtotal
                    span {{ subtotal | currency }}
            hr
            re-toast-alert.mb-4(v-if="bookingError" type="danger") {{ bookingError }}
            re-button.btn-danger.btn-lg(@click="bookNow" :loading="booking" :disabled="!fullBooking || ($v.bookingGroup.$dirty && $v.bookingGroup.$invalid)") Book Now
            .service-fee-hint The total cost above doesn't include ${{ serviceFee }} service fee.

</template>

<script>
import { mapGetters } from 'vuex'
import ComponentMixin from 'utils/vue/mixins/components/complex'
import FormMixin from 'utils/vue/mixins/form'
import { Validators, parseValidations, setValidationRule, parseValidationSetup } from 'utils/vue/lib/validations'
import { getFirstErrorMessage } from 'utils/vue/services/main'
import moment from 'moment'
import { BFormDatepicker } from 'bootstrap-vue'
import GmapAutocomplete from 'gmap-vue/dist/components/autocomplete-input'

const VALIDATIONS = {
    game: setValidationRule('fields.game', {
        required: { error: 'Game is required' }
    }),
    date: setValidationRule('fields.date', {
        required: { error: 'Date is required' }
    }),
    timeOfDay: setValidationRule('fields.timeOfDay', {
        required: { error: 'Time is required' }
    }),
    players: setValidationRule('fields.players', {
        required: { error: 'Players is required' }
    }),
    hours: setValidationRule('fields.hours', {
        required: { error: 'Hours is required' },
        minValue: { error: 'The minimum number of hours is 2', rule: Validators.minValue(2) },
    }),
    slot: setValidationRule('fields.slot', {
        required: { error: 'Time slot is required' }
    }),
    address: setValidationRule('fields.address', {
        required: { error: 'Address is required' }
    }),
}

const TIMES_OF_DAY = {
    morning: {
        value: 'morning',
        text: 'Morning',
        from: '6:00',
        to: '11:59'
    },
    afternoon: {
        value: 'afternoon',
        text: 'Afternoon',
        from: '12:00',
        to: '17:59'
    },
    evening: {
        value: 'evening',
        text: 'Evening',
        from: '18:00',
        to: '23:59'
    },
    night: {
        value: 'night',
        text: 'Night',
        from: '0:00',
        to: '5:59'
    },
}

const SLOT_LENGTH = 60

export default {
    name: 'booking',

    mixins: [
        ComponentMixin,
        FormMixin,
    ],

    components: {
        BFormDatepicker,
        GmapAutocomplete,
    },

    props: {
        profile: {
            type: Object,
            required: true,
        },
    },

    data() { return {
        fields: {
            game: null,
            date: null,
            timeOfDay: null, // 'morning',
            players: '1',
            hours: '2',
            slot: null,
            upgrades: {},
            address: '',
            notes: '',
        },
        location: {},
        timesOfDay: TIMES_OF_DAY,
        checkingAvailability: false,
        availableSlots: [],
        unavailable: false,
        fullBooking: false,
        timezone: null,
        bookingError: null,
        booking: false,
        disabledDates: [],
        availableTimesOfDay: [],
    }},

    watch: {
        'fields.game': function () {
            this.fields.upgrades = {}
        },
        'fields.date': function () {
            this.initialBooking()
            this.retrieveTimeSlots()
        },
        'fields.timeOfDay': function () {
            this.initialBooking()
        },
        'fields.players': function () {
            this.initialBooking()
        },
        'fields.hours': function () {
            this.initialBooking()
            this.retrieveDisabledDates()
        },
        'fields.address': function () {
            this.initialBooking()
        },
    },

    validations() {
        VALIDATIONS.hours.items.maxValue = { error: `The maximum number of hours is ${this.product.max_slots}`, rule: Validators.maxValue(this.product.max_slots) }
        VALIDATIONS.players.items.minValue = { error: `The minimum number of players is ${this.product.min_participants}`, rule: Validators.minValue(this.product.min_participants) }

        const fields = parseValidations(VALIDATIONS)
        return {
            fields,
            retrieveTimeSlotsGroup: [
                'fields.date', 'fields.hours',
            ],
            checkAvailabilityGroup: [
                'fields.game', 'fields.date', 'fields.timeOfDay', 'fields.players', 'fields.hours', 'fields.address',
            ],
            bookingGroup: [
                'fields.slot',
            ],
        }
    },

    computed: {
        ...mapGetters(['authCheck']),

        product() {
            return this.profile.product.data
        },
        games() {
            const gameTitles = Object.fromEntries(window.addiesaas.lairdnd.games.map(game => {
                return [game.name, game.title]
            }))

            return this.product.games
                .filter(game => gameTitles[game.name])
                .map(game => {
                    game.title = gameTitles[game.name]
                    return game
                })
        },
        upgrades() {
            return this.product.upgrades
        },
        subtotal() {
            const upgradesPrice = this.product.upgrades
                .filter(upgrade => this.fields.upgrades[upgrade.name])
                .map(upgrade => Number(upgrade.price))
                .reduce((a, c) => a + c, 0)

            return this.product.price * this.fields.hours + upgradesPrice
        },
        invalidAddress() {
            return this.$v.fields.address.$dirty && this.$v.fields.address.$invalid
        },
        invalidDate() {
            return this.$v.fields.date.$dirty && this.$v.fields.date.$invalid
        },
        maxHours() {
            return this.product.max_slots
        },
        minParticipants() {
            return this.product.min_participants
        },
        serviceFee() {
            return window.addiesaas.lairdnd.gamemaster.service_fee
        },
        minDate() {
            return moment().add(this.product.hours_in_advance, 'hours').startOf('day').toDate()
        },
        maxDate() {
            return moment().add(this.product.days_in_future, 'days').startOf('day').toDate()
        },
    },

    async created() {
        this.validations = parseValidationSetup(VALIDATIONS, this)
        this.setupForm(VALIDATIONS, { game: '', date: '', timeOfDay: '', players: '', hours: '', slot: null, address: '' })

        this.retrieveDisabledDates()
    },

    methods: {
        async retrieveDisabledDates() {
            if (this.$v.fields.hours.$invalid) {
                return
            }

            let { data: { unavailability }} = await axios.post('/api/customer/reservation/unavailability-dates', {
                product_id: this.product.id,
                participants: this.product.min_participants,
                slots: this.fields.hours,
                date_from: moment(this.minDate).format('Y-MM-DD'),
                date_to: moment(this.maxDate).format('Y-MM-DD'),
                time_slot_size: 60,
            })

            this.disabledDates = unavailability
        },

        async retrieveTimeSlots() {
            if (this.$v.retrieveTimeSlotsGroup.$invalid) {
                return
            }

            let { data: { unavailability, timezone }} = await axios.post('/api/customer/game-master-reservation-product/unavailability', {
                product_id: this.product.id,
                participants: this.product.min_participants,
                slots: this.fields.hours,
                date: this.fields.date,
                time_slot_size: 60,
                location: Object.assign({
                    latitude: this.product.location_latitude,
                    longitude: this.product.location_longitude,
                }, this.product.location)
            })

            unavailability = unavailability.map(this.minutes)

            this.availableTimesOfDay = Object.values(this.timesOfDay).filter(timeOfDay => {
                let slotOffset = this.minutes(timeOfDay.from)
                const lastSlotOffset = this.minutes(timeOfDay.to)
                do {
                    if (!unavailability.includes(slotOffset)) {
                        return true
                    }
                    slotOffset += SLOT_LENGTH
                } while (slotOffset < lastSlotOffset)
            })

            if (this.availableTimesOfDay.length > 0) {
                if (!this.availableTimesOfDay.find(timeOfDay => timeOfDay.value == this.fields.timeOfDay)) {
                    this.$set(this.fields, 'timeOfDay', this.availableTimesOfDay[0].value)
                }
            } else {
                this.$set(this.fields, 'timeOfDay', '')
            }
        },

        async checkAvailability() {
            this.$v.checkAvailabilityGroup.$touch()

            if (this.$v.checkAvailabilityGroup.$invalid) {
                return
            }

            try {
                this.checkingAvailability = true

                let { data: { unavailability, timezone }} = await axios.post('/api/customer/game-master-reservation-product/unavailability', {
                    product_id: this.product.id,
                    participants: this.fields.players,
                    slots: this.fields.hours,
                    date: this.fields.date,
                    time_slot_size: 60,
                    location: this.location
                })

                unavailability = unavailability.map(this.minutes)

                let slotOffset = this.minutes(this.timesOfDay[this.fields.timeOfDay].from)
                const lastSlotOffset = this.minutes(this.timesOfDay[this.fields.timeOfDay].to)

                const availableSlots = {}

                do {
                    if (!unavailability.includes(slotOffset)) {
                        const slotStart = moment().startOf('day').add(slotOffset, 'm')
                        const slotEnd = moment(slotStart).add(this.fields.hours, 'h')
                        availableSlots[slotOffset] = slotStart.format('h:mm A') + ' - ' + slotEnd.format('h:mm A')
                    }

                    slotOffset += SLOT_LENGTH
                } while (slotOffset < lastSlotOffset)

                this.availableSlots = availableSlots

                this.unavailable = !Object.values(this.availableSlots).length
                this.fullBooking = !this.unavailable

                this.timezone = timezone
                this.location.timezone_id = timezone.id
                this.location.timezone_name = timezone.name
                this.location.timezone_offset = timezone.offset
            } finally {
                this.checkingAvailability = false
            }
        },

        async bookNow() {
            this.$v.bookingGroup.$touch()

            if (this.$v.bookingGroup.$invalid) {
                return
            }

            if (!this.authCheck) {
                this.bookingError = 'You must be logged in to continue'
                return
            }

            const startAt = moment
                .utc(this.fields.date)
                .add(-this.timezone.offset, 's')
                .add(this.fields.slot, 'm')
                .format('YYYY-MM-DD HH:mm')

            const upgrades = Object.fromEntries(
                Object.entries(this.fields.upgrades)
                    .filter(([upgrade, include]) => include)
                    .map(([upgrade, include]) => [upgrade, 1])
            )

            const gameTitle = window.addiesaas.lairdnd.games.find(game => game.name == this.fields.game).title

            const addonTitles = Object.fromEntries(Object.keys(this.fields.upgrades)
                .map(key => [key, this.upgrades.find(upgrade => upgrade.name == key).title]))

            const orderItem = {
                product_id: this.product.id,
                options: {
                    rate: 'default',
                    start_at: startAt,
                    participants: this.fields.players,
                    slots: this.fields.hours,
                    location: this.location,
                    upgrades,
                    addon_titles: addonTitles,
                    game: this.fields.game,
                    game_title: gameTitle,
                    notes: this.fields.notes,
                },
                deposit: true,
            }

            const payload = {
                items: [orderItem],
            }

            try {
                this.booking = true
                this.bookingError = null

                const { data: { data: response } } = await axios.post('/api/customer/order/validate', payload)

                this.$emit('book', { payload, response })
            } catch (e) {
                this.bookingError = getFirstErrorMessage(e)
            } finally {
                this.booking = false
            }
        },

        minutes(time) {
            const [hour, minute] = time.split(':')
            return parseInt(hour) * 60 + parseInt(minute)
        },

        initialBooking() {
            this.fullBooking = false
            this.unavailable = false
            this.fields.slot = null
        },

        addressChanged(place) {
            this.location.latitude = place.geometry.location.lat()
            this.location.longitude = place.geometry.location.lng()
            this.location.address = place.formatted_address
            this.location.place_id = place.place_id
            this.fields.address = place.formatted_address
            this.$v.fields.address.$reset()
        },

        setDate(date) {
            this.$set(this.fields, 'date', moment(date).format('YYYY-MM-DD'))
        },

        dateDisabled(ymd, date) {
            return this.disabledDates.includes(ymd)
        },
    }
}
</script>
